(ns leg.app
  "The window: three panes on a screen that has room for them, one at a
  time on a screen that does not.

  Feeds, articles, the article. It is the shape every reader has had since
  mail readers had it, and it is the right one: what to read is chosen from
  left to right, and the thing being read keeps its own column so the list
  does not move while somebody reads.

  A phone gets the same three, one at a time, and moving between them is
  what selecting something already means - so there is no navigation to
  learn and nothing to put a hamburger in front of.

  Two of the three are addresses (`leg.route`), so the browser's own back
  button leads out of an article, then out of the feed, then out of the
  reader - which is the way back everybody already knows.

  Nothing is drawn along the top edge of any of them. Every pane and every
  page carries the same bar at its foot instead (`leg.nav`), with [Home] at
  the left end of it: that is where a thumb is on a phone, and an installed
  app has no address bar to reach for above it."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [kc.core :as kc]
            [leg.admin :as admin]
            [leg.article :as article]
            [leg.articles :as articles]
            [leg.dialogs :as dialogs]
            [leg.keys :as keys]
            [leg.mui :as mui :refer [sx]]
            [leg.settings :as settings]
            [leg.sidebar :as sidebar]
            [leg.state :as state]))

(def poll-seconds
  "How often the counts are asked for again. A reader is not a chat: what
  changes by itself is a number in the sidebar, and the server only fetches
  every fifteen minutes anyway."
  120)

(def-ui Error
  "What went wrong, over the window and out of the way of the reading. It
  stays until it is dismissed - an error that fades is one nobody read."
  [error]
  (when error
    ($ mui/Box {:sx (sx {:position "fixed" :left 0 :right 0 :bottom 0 :zIndex 10
                         :padding ".5rem"})}
       ($ mui/Alert {:severity "error" :variant "filled"
                     :onClose (fn [_] (state/dismiss-error!))}
          error))))

(def-ui Wide []
  ($ mui/Box {:sx (sx {:display "grid"
                       :gridTemplateColumns "16rem 24rem 1fr"
                       ;; One row, and it is the window - said here rather
                       ;; than left implicit. An implicit row is sized by
                       ;; what is in it, so the tallest column decided how
                       ;; tall all three were: a long feed list or a long
                       ;; article pushed the row past the screen, the
                       ;; `overflow: hidden` above cut the surplus off, and
                       ;; the columns' own `overflow: auto` never had
                       ;; anything to scroll because their boxes had grown
                       ;; to fit. `minmax(0, 1fr)` is the screen and nothing
                       ;; else; the zero is what stops the content from
                       ;; raising the floor again.
                       :gridTemplateRows "minmax(0, 1fr)"
                       :height "100vh" :overflow "hidden"})}
     ($ mui/Box {:className "leser-paper"
                 :sx (sx {:borderRight "1px solid" :borderColor "divider"
                          :backgroundColor "background.default"
                          :minHeight 0 :overflow "hidden"})}
        ($ sidebar/SidebarPane))
     ($ mui/Box {:sx (sx {:borderRight "1px solid" :borderColor "divider"
                          :display "flex" :flexDirection "column"
                          :minHeight 0 :overflow "hidden"})}
        ($ articles/ArticlesPane {:main? false}))
     ($ article/ArticlePane {:narrow? false})))

(def-ui Narrow [state]
  (let [{:keys [pane]} state]
    ($ mui/Box {:sx (sx {:height "100vh" :display "flex" :flexDirection "column"
                         :overflow "hidden"
                         ;; As in `Wide`: whatever pane is on the screen may
                         ;; not be taller than the screen. A flex item's
                         ;; automatic minimum is its content, which for a
                         ;; column of articles is metres.
                         "& > *" {:minHeight 0}})}
       (case pane
         :feeds   ($ sidebar/SidebarPane)
         :article ($ article/ArticlePane {:narrow? true})
         ($ articles/ArticlesPane {:main? true})))))

(def-ui App [state]
  (let [narrow? (mui/use-narrow?)]
    ;; The counts again, every couple of minutes, for as long as this
    ;; window is open. Cleared on the way out, or a window that was closed
    ;; would go on asking.
    (kc/use-effect
     []
     (let [t (js/setInterval state/poll! (* 1000 poll-seconds))]
       (fn [] (js/clearInterval t))))
    ;; One listener for the whole window, installed once (see leg.keys).
    (kc/use-effect
     []
     (.addEventListener js/window "keydown" keys/handle!)
     (fn [] (.removeEventListener js/window "keydown" keys/handle!)))
    ($ :<>
       ;; A page is a place of its own and takes the whole window - nothing
       ;; of the reading is drawn behind it, and nothing of it is drawn
       ;; while somebody is reading. One at a time, and the reader is what
       ;; is left when there is none.
       (case (:page state)
         :settings ($ settings/SettingsPane)
         :admin    ($ admin/AdminPane)
         (if narrow?
           ($ Narrow {:state state})
           ($ Wide)))
       ;; Over whatever that was: every field somebody types into is a form
       ;; laid over the page it was opened from (`leg.dialogs`).
       ($ dialogs/Dialogs)
       ($ Error {:error (:error state)}))))
